import React from 'react';
import { Alert, theme } from 'antd';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { buildParcelSlaMonitoringUrl, warehouseLocalDate } from '../../../../utils/parcelSlaMonitoringLink';

interface BatchPushEntryHintProps {
    warehouseId: number;
    /** 仓库准入节点（看板返回，PlanningEntryNode 名称：HUB_DEPARTURE / WHS_ARRIVAL / DEST_HUB_ARRIVAL） */
    entryNode: string | null | undefined;
    /** 仓库 IANA 时区（看板返回的 timezoneLocation），用于算仓库当地当天 */
    timezone: string | null | undefined;
}

/** 已登记文案的准入节点；其余值（理论上不会出现）不渲染横幅 */
const ENTRY_NODE_KEYS = ['HUB_DEPARTURE', 'WHS_ARRIVAL', 'DEST_HUB_ARRIVAL'];

/**
 * 批次推送页横幅（分批次-尾程）：提示本仓按准入节点（whs到件 / HUB发件，加粗）进入待推送池，
 * 「历史包裹追踪」跳转仓管侧历史包裹追踪（当前仓、仓库当地当天、「到达HUB时间」视图，与线路页同一深链）。
 * 样式对齐破损面单查询的提示条：info 类型 Alert，链接为主题链接色（同 Typography.Link），站内路由跳转。
 * 时区缺失时链接文字只展示、不跳转；准入节点未知时不展示。
 */
const BatchPushEntryHint: React.FC<BatchPushEntryHintProps> = ({ warehouseId, entryNode, timezone }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    if (!entryNode || !ENTRY_NODE_KEYS.includes(entryNode)) return null;
    const localDate = warehouseLocalDate(timezone);
    // 标签名不能用 link：它是 HTML 空元素，i18next 解析时会当成自闭合，文字落到链接外
    const jump = localDate
        ? <Link
            to={buildParcelSlaMonitoringUrl(warehouseId, localDate, false)}
            style={{ color: token.colorLink }}
            data-testid="batch-push-entry-hint-link"
        />
        : <span />;
    return (
        <Alert
            type="info"
            showIcon
            // 与下方「待推送」统计行间距收紧（16px → 8px）
            className="mb-2"
            data-testid="batch-push-entry-hint"
            message={
                <span>
                    <Trans
                        i18nKey="wms.batchPush.entryHint"
                        values={{ node: t(`wms.batchPush.entryNode.${entryNode}`) }}
                        components={{ strong: <b />, jump }}
                    />
                </span>
            }
        />
    );
};

export default BatchPushEntryHint;
